/**
 * REAL-TIME RECOMMENDATIONS HOOK
 *
 * Orchestrates real-time recommendation updates with smart polling.
 * Integrates: deadlines, liability analysis, driver invoices
 *
 * SMART POLLING: Only recalculates full recommendation when urgency level changes.
 * Otherwise, just updates deadlines (lightweight, no API calls).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Ticket, Hire, Driver, Organization } from '../types';
import { RecommendationService, type RecommendationResult } from '../services/recommendationService';
import { calculateTicketDeadlines, type TicketDeadlines } from '../services/deadlineService';
import { analyzeLiability, type LiabilityDecision } from '../services/liabilityEngine';
import { getDriverInvoiceStatusForTicket, type DriverInvoiceStatus } from '../services/driverInvoiceService';
import { RecoveryDecisionService, type BillingSettingsSnapshot } from '../services/recoveryDecisionService';

interface UseRealtimeRecommendationsReturn {
  recommendation: RecommendationResult | null;
  deadlines: TicketDeadlines | null;
  liabilityDecision: LiabilityDecision | null;
  loading: boolean;
  error: string | null;
  recalculate: () => Promise<void>;
}

/**
 * Hook for real-time FleetMind recommendations
 *
 * Features:
 * - Initial calculation on mount and when dependencies change
 * - Smart polling (60s interval, only full recalc if urgency changes)
 * - Orchestrates deadline + liability + invoice data
 * - Lightweight deadline updates between full recalculations
 */
export function useRealtimeRecommendations(
  ticket: Ticket | null,
  linkedHire: Hire | null,
  linkedDriver: Driver | null,
  organization: Organization | null
): UseRealtimeRecommendationsReturn {
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null);
  const [deadlines, setDeadlines] = useState<TicketDeadlines | null>(null);
  const [liabilityDecision, setLiabilityDecision] = useState<LiabilityDecision | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track previous urgency level to detect changes without causing re-renders
  const previousUrgencyRef = useRef<'safe' | 'warning' | 'urgent' | 'expired' | null>(null);

  // Wrap calculateRecommendation in useCallback to avoid dependency issues
  const calculateRecommendation = useCallback(async () => {
    if (!ticket) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Calculate deadlines (local, fast)
      const calculatedDeadlines = calculateTicketDeadlines(ticket);
      setDeadlines(calculatedDeadlines);

      // Update ref for urgency tracking
      previousUrgencyRef.current = calculatedDeadlines.nextDeadline?.urgencyLevel || null;

      // 2. Fetch liability decision (API call, cached)
      let liability: LiabilityDecision | null = null;
      try {
        const result = await analyzeLiability(ticket.id, ticket.organisation_id);
        liability = result.decision;
        setLiabilityDecision(liability);
      } catch (err) {
        console.warn('[useRealtimeRecommendations] Failed to fetch liability decision:', err);
        // Continue without liability decision
      }

      // 3. Fetch driver invoice status (API call)
      let invoiceStatus: DriverInvoiceStatus | null = null;
      try {
        invoiceStatus = await getDriverInvoiceStatusForTicket(ticket.id);
      } catch (err) {
        console.warn('[useRealtimeRecommendations] Failed to fetch invoice status:', err);
        // Continue without invoice status
      }

      // 4. Fetch billing settings for exact recovery/admin-fee context
      let billingSettings: BillingSettingsSnapshot | null = null;
      try {
        billingSettings = await RecoveryDecisionService.getBillingSettingsForOrganisation(ticket.organisation_id);
      } catch (err) {
        console.warn('[useRealtimeRecommendations] Failed to fetch billing settings:', err);
      }

      const recoveryDecision = RecoveryDecisionService.decideRecoveryForTicket(ticket, {
        linkedHire,
        linkedDriver,
        invoiceStatus,
        billingSettings
      });

      // 5. Generate recommendation with all context
      const result = RecommendationService.analyze(
        ticket,
        linkedHire,
        linkedDriver,
        organization,
        liability,
        calculatedDeadlines,
        invoiceStatus,
        recoveryDecision
      );

      setRecommendation(result);
    } catch (err: any) {
      console.error('[useRealtimeRecommendations] Error calculating recommendation:', err);
      setError(err.message || 'Failed to calculate recommendation');
    } finally {
      setLoading(false);
    }
  }, [ticket, linkedHire, linkedDriver, organization]);

  // Calculate on mount + when dependencies change
  useEffect(() => {
    if (!ticket) return;
    calculateRecommendation();
  }, [ticket?.id, linkedHire?.id, linkedDriver?.id, organization?.id, calculateRecommendation]);

  // Real-time polling: Recalculate deadlines every 60s
  // Only triggers full recalc if urgency level changes
  useEffect(() => {
    if (!ticket) return;

    const interval = setInterval(() => {
      const newDeadlines = calculateTicketDeadlines(ticket);

      // Check if urgency changed (e.g., crossed from 'warning' to 'urgent')
      const oldUrgency = previousUrgencyRef.current;
      const newUrgency = newDeadlines.nextDeadline?.urgencyLevel || null;

      if (oldUrgency !== newUrgency) {
        console.log(`[useRealtimeRecommendations] Urgency changed: ${oldUrgency} → ${newUrgency}, recalculating...`);
        calculateRecommendation();
      } else {
        // Just update deadlines (lightweight)
        setDeadlines(newDeadlines);
      }
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [ticket, calculateRecommendation]);

  return {
    recommendation,
    deadlines,
    liabilityDecision,
    loading,
    error,
    recalculate: calculateRecommendation
  };
}
